<template>
	<div class="input-wrapper">
		<div :class="classes" @click="focusOnInputEl">
			<div class="input-col">
				<label class="input-label" :for="id">
					{{ label ?? placeholder }}
				</label>

				<input
					:id
					:name
					:disabled
					:required
					:placeholder
					:maxlength
					type="text"
					ref="inputEl"
					class="input"
					v-model.trim="model"
					@blur="handleBlur"
					@change="emits('change', $event)"
					@input="emits('input', $event)"
					@focus="handleFocus($event)"
				/>
			</div>

			<div v-if="hint" class="input-hint">{{ hint }}</div>
		</div>

		<p v-if="error && !disabled" class="input-error">{{ error }}</p>
	</div>
</template>

<script setup lang="ts">
import Inputmask from 'inputmask';

import { getRandomId } from '@/utils/index';

const INPUT_SIZES = ['lg', 'xl'] as const;
type ShInputSizesT = 'lg' | 'xl';
type ShInputSizeName = `size-${(typeof INPUT_SIZES)[number]}`;
type ShInputSizeNameAndValue = `size-${(typeof INPUT_SIZES)[number]}${number}`;
type UISizeBckPoint = ShInputSizeName | ShInputSizeNameAndValue;

export type ShInputText = {
	name?: string;
	label?: string;
	disabled?: boolean;
	required?: boolean;
	placeholder?: string;
	focusOnMount?: boolean;
	error?: string;
	hint?: string;
	maxlength?: number;
	mask?: string;
} & { [s in ShInputSizesT]?: number | boolean };

const props = withDefaults(defineProps<ShInputText>(), {
	maxlength: Number.MAX_SAFE_INTEGER
});

const model = defineModel<string>({ required: true, default: '' });
const emits = defineEmits(['blur', 'change', 'input', 'focus']);

const id = ref('');
const isFocused = ref(false);
const inputEl = useTemplateRef('inputEl');

const classes = computed(() => {
	const sizeClasses = getSizeClass();

	const hasValue = model.value.length;

	return {
		'input-row': true,
		filled: hasValue,
		required: props.required,
		invalid: props.error?.length,
		disabled: props.disabled,
		...sizeClasses
	};
});

onMounted(() => {
	id.value = getRandomId();

	if (props.focusOnMount) {
		focusOnInputEl();
	}

	// if (inputEl.value && props.mask) {
	// 	Inputmask(props.mask).mask(inputEl.value);
	// }
});

function handleFocus($event: FocusEvent) {
	emits('focus', $event);
	isFocused.value = true;
}

function handleBlur($event: FocusEvent) {
	emits('blur', $event);
	isFocused.value = false;
}

function focusOnInputEl() {
	inputEl.value?.focus();
}

function getSizeClass() {
	const classes: { [key in UISizeBckPoint]?: boolean } = {};

	INPUT_SIZES.forEach((s) => {
		const value = props[s];

		if (!(s in props)) return;
		if (value === undefined) return;

		if (typeof value === 'boolean') {
			return (classes[`size-${s}`] = value);
		}

		classes[`size-${s}${value}`] = true;
	});

	return classes;
}
</script>

<style scoped lang="scss"></style>
